/**
 * React Query hooks for electoral metadata API.
 */
import {
  useQuery,
  useMutation,
  useQueryClient
} from "@tanstack/react-query";
import {
  fetchElectoralMetadataFields,
  createElectoralMetadataField,
  updateElectoralMetadataField,
  deleteElectoralMetadataField,
  reorderElectoralMetadataFields,
  checkElectoralMetadataFieldsAccess,
  checkMunicipalityMetadataCanEdit,
  fetchMunicipalityMetadataGrid,
  upsertElectoralMetadataValue,
  upsertElectoralMetadataValuesBatch,
  importElectoralMetadataCsv
} from "./api";
import type {
  CreateElectoralMetadataFieldRequest,
  UpdateElectoralMetadataFieldRequest,
  UpsertElectoralMetadataValueRequest,
  UpsertElectoralMetadataValuesBatchRequest
} from "./types";

export const electoralMetadataFieldsQueryKey = [
  "electoral-metadata",
  "fields"
] as const;

export const municipalityMetadataGridQueryKey = (municipalityId: string) =>
  ["electoral-metadata", "municipalities", municipalityId] as const;

export function useElectoralMetadataFieldsAccess() {
  return useQuery({
    queryKey: ["electoral-metadata", "fields", "access"],
    queryFn: checkElectoralMetadataFieldsAccess,
    retry: false,
    staleTime: 5 * 60 * 1000 // 5 minutes
  });
}

export function useElectoralMetadataFields(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: electoralMetadataFieldsQueryKey,
    queryFn: fetchElectoralMetadataFields,
    enabled: options?.enabled !== false
  });
}

export function useCreateElectoralMetadataField() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: CreateElectoralMetadataFieldRequest) =>
      createElectoralMetadataField(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: electoralMetadataFieldsQueryKey });
    }
  });
}

export function useUpdateElectoralMetadataField() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      fieldId,
      request
    }: {
      fieldId: string;
      request: UpdateElectoralMetadataFieldRequest;
    }) => updateElectoralMetadataField(fieldId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: electoralMetadataFieldsQueryKey });
    }
  });
}

export function useDeleteElectoralMetadataField() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fieldId: string) => deleteElectoralMetadataField(fieldId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: electoralMetadataFieldsQueryKey });
    }
  });
}

export function useReorderElectoralMetadataFields() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (orderedFieldIds: string[]) =>
      reorderElectoralMetadataFields(orderedFieldIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: electoralMetadataFieldsQueryKey });
    }
  });
}

export function useMunicipalityMetadataGrid(
  municipalityId: string | null,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: municipalityMetadataGridQueryKey(municipalityId ?? ""),
    queryFn: () => fetchMunicipalityMetadataGrid(municipalityId!),
    enabled: (options?.enabled !== false && !!municipalityId)
  });
}

export function useMunicipalityMetadataCanEdit(municipalityId: string | null) {
  return useQuery({
    queryKey: ["electoral-metadata", "municipalities", municipalityId ?? "", "can-edit"],
    queryFn: () => checkMunicipalityMetadataCanEdit(municipalityId!),
    enabled: !!municipalityId,
    staleTime: 5 * 60 * 1000
  });
}

export function useUpsertElectoralMetadataValue(municipalityId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: UpsertElectoralMetadataValueRequest) =>
      upsertElectoralMetadataValue(request),
    onSuccess: () => {
      if (municipalityId) {
        queryClient.invalidateQueries({
          queryKey: municipalityMetadataGridQueryKey(municipalityId)
        });
      }
    }
  });
}

export function useUpsertElectoralMetadataValuesBatch(
  municipalityId: string | null
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: UpsertElectoralMetadataValuesBatchRequest) =>
      upsertElectoralMetadataValuesBatch(request),
    onSuccess: () => {
      if (municipalityId) {
        queryClient.invalidateQueries({
          queryKey: municipalityMetadataGridQueryKey(municipalityId)
        });
      }
    }
  });
}

export function useImportElectoralMetadataCsv(municipalityId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) =>
      importElectoralMetadataCsv(municipalityId!, file),
    onSuccess: () => {
      if (municipalityId) {
        queryClient.invalidateQueries({
          queryKey: municipalityMetadataGridQueryKey(municipalityId)
        });
      }
    }
  });
}
